/**
 * Double Ratchet (Signal) — per-message forward secrecy and post-compromise
 * security. Combines:
 *   - a **DH ratchet**: each time a new DH key is received from the peer a new
 *     pair is generated and the root key is advanced (heals a past compromise);
 *   - two **symmetric chains** (send/receive): each message derives its
 *     own message key that is DISCARDED after use (a leaked message does not
 *     compromise the others).
 *
 * Tolerates **out-of-order and losses**: "skipped" message keys are stored
 * (bounded by `MAX_SKIP`) to be able to decrypt messages that arrive late.
 *
 * KDF_RK(rk, dh)  = HKDF(salt=rk, ikm=dh)        → (rk', ck)
 * KDF_CK(ck)      = HMAC(ck,0x02), HMAC(ck,0x01) → (ck', mk)
 * encryption      = AES-256-GCM(mk, pt, aad = AD || header)
 */

import { bytesToBase64, concatBytes } from "./crypto/encoding";
import { dh, generateDhKeyPair, type RawKeyPair } from "./crypto/dh";
import { aeadOpen, aeadSeal, hkdfExpand, hmac, importAesKey } from "./crypto/primitives";

const RK_INFO = "susurro:ratchet:root:v1";
const MAX_SKIP = 256; // anti-DoS limit of skipped keys per chain

export interface RatchetHeader {
  readonly dhPub: Uint8Array;
  /** No. of messages in the PREVIOUS sending chain (previous chain length). */
  readonly pn: number;
  /** Message number within the current chain. */
  readonly n: number;
}

export interface RatchetMessage {
  readonly header: RatchetHeader;
  readonly ciphertext: Uint8Array;
}

export interface RatchetState {
  rootKey: Uint8Array;
  sending?: RawKeyPair;
  receivingPub?: Uint8Array;
  sendChain?: Uint8Array;
  recvChain?: Uint8Array;
  sendCount: number;
  recvCount: number;
  prevSendCount: number;
  /** skipped message keys: base64(dhPub)+":"+n → mk. */
  skipped: Map<string, Uint8Array>;
}

// --- KDFs -------------------------------------------------------------------

async function kdfRoot(rootKey: Uint8Array, dhOut: Uint8Array): Promise<[Uint8Array, Uint8Array]> {
  const out = await hkdfExpand(dhOut, RK_INFO, 64, rootKey);
  return [out.slice(0, 32), out.slice(32, 64)];
}

async function kdfChain(chainKey: Uint8Array): Promise<[Uint8Array, Uint8Array]> {
  const nextChain = await hmac(chainKey, new Uint8Array([0x02]));
  const messageKey = await hmac(chainKey, new Uint8Array([0x01]));
  return [nextChain, messageKey];
}

function serializeHeader(h: RatchetHeader): Uint8Array {
  const meta = new Uint8Array(8);
  new DataView(meta.buffer).setUint32(0, h.pn, false);
  new DataView(meta.buffer).setUint32(4, h.n, false);
  return concatBytes(h.dhPub, meta);
}

async function encryptWith(
  messageKey: Uint8Array,
  plaintext: Uint8Array,
  associatedData: Uint8Array,
  header: RatchetHeader,
): Promise<Uint8Array> {
  const key = await importAesKey(messageKey, ["encrypt"]);
  const aad = bytesToBase64(concatBytes(associatedData, serializeHeader(header)));
  return aeadSeal(key, plaintext, aad);
}

async function decryptWith(
  messageKey: Uint8Array,
  ciphertext: Uint8Array,
  associatedData: Uint8Array,
  header: RatchetHeader,
): Promise<Uint8Array> {
  const key = await importAesKey(messageKey, ["decrypt"]);
  const aad = bytesToBase64(concatBytes(associatedData, serializeHeader(header)));
  return aeadOpen(key, ciphertext, aad);
}

// --- Initialization ---------------------------------------------------------

/** Initiator (Alice): already has SK and the peer's public DH key (Bob's SPK). */
export async function initRatchetInitiator(
  sharedSecret: Uint8Array,
  peerDhPub: Uint8Array,
): Promise<RatchetState> {
  const sending = await generateDhKeyPair();
  const [rootKey, sendChain] = await kdfRoot(sharedSecret, await dh(sending.privateKey, peerDhPub));
  return {
    rootKey,
    sending,
    receivingPub: peerDhPub,
    sendChain,
    sendCount: 0,
    recvCount: 0,
    prevSendCount: 0,
    skipped: new Map(),
  };
}

/** Responder (Bob): uses its own DH pair (its SPK) and does not know the peer's yet. */
export function initRatchetResponder(sharedSecret: Uint8Array, selfDh: RawKeyPair): RatchetState {
  return {
    rootKey: sharedSecret,
    sending: selfDh,
    sendCount: 0,
    recvCount: 0,
    prevSendCount: 0,
    skipped: new Map(),
  };
}

// --- Encrypt / decrypt ------------------------------------------------------

export async function ratchetEncrypt(
  state: RatchetState,
  plaintext: Uint8Array,
  associatedData: Uint8Array,
): Promise<RatchetMessage> {
  if (!state.sendChain || !state.sending) {
    throw new Error("ratchet: no sending chain (not yet received from peer)");
  }
  const [nextChain, messageKey] = await kdfChain(state.sendChain);
  state.sendChain = nextChain;
  const header: RatchetHeader = {
    dhPub: state.sending.publicKey,
    pn: state.prevSendCount,
    n: state.sendCount,
  };
  state.sendCount += 1;
  const ciphertext = await encryptWith(messageKey, plaintext, associatedData, header);
  return { header, ciphertext };
}

function skipKey(dhPub: Uint8Array, n: number): string {
  return `${bytesToBase64(dhPub)}:${n}`;
}

async function skipMessageKeys(state: RatchetState, until: number): Promise<void> {
  if (!state.recvChain || state.receivingPub === undefined) {
    return;
  }
  if (until - state.recvCount > MAX_SKIP) {
    throw new Error("ratchet: demasiadas claves saltadas (posible DoS)");
  }
  while (state.recvCount < until) {
    const [nextChain, messageKey] = await kdfChain(state.recvChain);
    state.recvChain = nextChain;
    state.skipped.set(skipKey(state.receivingPub, state.recvCount), messageKey);
    state.recvCount += 1;
  }
}

async function dhRatchet(state: RatchetState, header: RatchetHeader): Promise<void> {
  state.prevSendCount = state.sendCount;
  state.sendCount = 0;
  state.recvCount = 0;
  state.receivingPub = header.dhPub;
  const current = state.sending;
  if (!current) {
    throw new Error("ratchet: sin par DH propio");
  }
  const [rk1, recvChain] = await kdfRoot(state.rootKey, await dh(current.privateKey, header.dhPub));
  state.rootKey = rk1;
  state.recvChain = recvChain;
  const nextSending = await generateDhKeyPair();
  const [rk2, sendChain] = await kdfRoot(
    state.rootKey,
    await dh(nextSending.privateKey, header.dhPub),
  );
  state.rootKey = rk2;
  state.sendChain = sendChain;
  state.sending = nextSending;
}

export async function ratchetDecrypt(
  state: RatchetState,
  message: RatchetMessage,
  associatedData: Uint8Array,
): Promise<Uint8Array> {
  const { header, ciphertext } = message;

  // 1) is it a skipped key already stored? (out-of-order message)
  const sk = skipKey(header.dhPub, header.n);
  const cached = state.skipped.get(sk);
  if (cached) {
    state.skipped.delete(sk);
    return decryptWith(cached, ciphertext, associatedData, header);
  }

  // 2) new DH key from the peer? → store skipped ones of the old chain + DH ratchet
  const isNewDh =
    state.receivingPub === undefined ||
    bytesToBase64(header.dhPub) !== bytesToBase64(state.receivingPub);
  if (isNewDh) {
    if (state.recvChain) {
      await skipMessageKeys(state, header.pn);
    }
    await dhRatchet(state, header);
  }

  // 3) skip to the message index and derive its key
  await skipMessageKeys(state, header.n);
  if (!state.recvChain) {
    throw new Error("ratchet: no receiving chain");
  }
  const [nextChain, messageKey] = await kdfChain(state.recvChain);
  state.recvChain = nextChain;
  state.recvCount += 1;
  return decryptWith(messageKey, ciphertext, associatedData, header);
}
